/**
 * Terrain chunk windowing math — pure, testable, no Three.js dependency.
 *
 * Used by the Commons chunk streamer in EnvironmentModule to compute which
 * terrain chunks to load/unload as the player moves through the world.
 */

/** Integer grid coordinates identifying a terrain chunk. */
export interface ChunkKey { cx: number; cz: number; }

/**
 * Map a world-space coordinate to the integer chunk index it falls in.
 * `Math.floor(coord / size)` — negative coordinates correctly land in the
 * chunk to the left of the origin (e.g. chunkOf(-1, 256) === -1).
 */
export function chunkOf(coord: number, size: number): number {
  return Math.floor(coord / size);
}

/**
 * Produce the (2r+1)² square window of chunk keys centred on (cx, cz).
 * Iterates Z-outer, X-inner (row-major).
 */
export function chunkWindow(cx: number, cz: number, radius: number): ChunkKey[] {
  const out: ChunkKey[] = [];
  for (let dz = -radius; dz <= radius; dz++) {
    for (let dx = -radius; dx <= radius; dx++) {
      out.push({ cx: cx + dx, cz: cz + dz });
    }
  }
  return out;
}

/**
 * Diff two chunk windows and return the keys to add (in `next` but not `prev`)
 * and the keys to remove (in `prev` but not `next`).
 */
export function diffWindows(
  prev: ChunkKey[],
  next: ChunkKey[],
): { add: ChunkKey[]; remove: ChunkKey[] } {
  const key = (k: ChunkKey): string => `${k.cx},${k.cz}`;
  const ps = new Set(prev.map(key));
  const ns = new Set(next.map(key));
  return {
    add: next.filter((k) => !ps.has(key(k))),
    remove: prev.filter((k) => !ns.has(key(k))),
  };
}
